import { supabase } from "./supabase";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "./supabaseConfig";

export const profileService = {
  // Get profile with all details
  async getProfileWithDetails(userId) {
    try {
      const { data, error } = await supabase.rpc("get_profile_with_details", {
        user_id: userId,
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error fetching profile details:", error);
      throw error;
    }
  },

  // Update profile information
  async updateProfileInfo(userId, profileData) {
    try {
      // Map frontend field names to database field names
      const mappedData = {};

      if (profileData.fullName !== undefined) {
        mappedData.full_name = profileData.fullName;
      }
      if (profileData.phoneNumber !== undefined) {
        mappedData.phone_number = profileData.phoneNumber;
      }
      if (profileData.country !== undefined) {
        mappedData.country = profileData.country;
      }
      if (profileData.addressLine1 !== undefined) {
        mappedData.address_line_1 = profileData.addressLine1;
      }
      if (profileData.addressLine2 !== undefined) {
        mappedData.address_line_2 = profileData.addressLine2;
      }
      if (profileData.city !== undefined) {
        mappedData.city = profileData.city;
      }
      if (profileData.state !== undefined) {
        mappedData.state = profileData.state;
      }
      if (profileData.postalCode !== undefined) {
        mappedData.postal_code = profileData.postalCode;
      }
      if (profileData.profile_photo_url !== undefined) {
        mappedData.profile_photo_url = profileData.profile_photo_url;
      }

      console.log("Mapped data being sent to database:", mappedData);

      const { data, error } = await supabase.rpc("update_profile_info", {
        user_id: userId,
        profile_data: mappedData,
      });

      console.log("Database response:", { data, error });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  },

  // Request email change
  async requestEmailChange(userId, newEmail) {
    try {
      const { data, error } = await supabase.rpc(
        "request_email_change_simple",
        {
          user_id: userId,
          new_email: newEmail,
        }
      );

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error);
      }

      // Get current user email for the email template
      const { data: currentUser } = await supabase.auth.getUser();
      const currentEmail = currentUser?.user?.email;

      // Send verification code via email
      await this.sendEmailChangeCode(newEmail, data.code, currentEmail);

      return {
        success: true,
        message: "Verification code sent to your new email address",
        code: data.code,
        expiresAt: data.expires_at,
      };
    } catch (error) {
      console.error("Error requesting email change:", error);
      throw error;
    }
  },

  // Send email change verification code
  async sendEmailChangeCode(newEmail, code, currentEmail) {
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/send-email-change-code`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: newEmail,
            code: code,
            current_email: currentEmail,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send verification code");
      }

      return {
        success: true,
        message: "Verification code sent successfully",
      };
    } catch (error) {
      console.error("Error sending email change code:", error);
      // Don't throw error here - we still want the code generation to succeed
      // even if email sending fails
      console.warn("Email sending failed, but verification code was generated");
    }
  },

  // Verify email change
  async verifyEmailChange(code) {
    try {
      const { data, error } = await supabase.rpc("verify_email_change_simple", {
        verification_code: code,
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error);
      }

      return {
        success: true,
        message: data.message,
        newEmail: data.new_email,
        userId: data.user_id,
      };
    } catch (error) {
      console.error("Error verifying email change:", error);
      throw error;
    }
  },

  // Cancel email change
  async cancelEmailChange(userId) {
    try {
      const { data, error } = await supabase.rpc("cancel_email_change", {
        user_id: userId,
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error cancelling email change:", error);
      throw error;
    }
  },

  // Upload profile photo
  async uploadProfilePhoto(userId, file) {
    try {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        throw new Error("Please upload a valid image file");
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("Image size must be less than 5MB");
      }

      // Generate unique filename with user folder structure
      const fileExt = file.name.split(".").pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;

      // Upload to Supabase storage
      const { error } = await supabase.storage
        .from("profile-photos")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: true, // Allow overwriting
        });

      if (error) throw error;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("profile-photos").getPublicUrl(fileName);

      // Update profile with photo URL
      const updateResult = await this.updateProfileInfo(userId, {
        profile_photo_url: publicUrl,
      });

      if (!updateResult.success) {
        throw new Error(updateResult.error);
      }

      return {
        success: true,
        photoUrl: publicUrl,
        message: "Profile photo updated successfully",
      };
    } catch (error) {
      console.error("Error uploading profile photo:", error);
      throw error;
    }
  },

  // Remove profile photo
  async removeProfilePhoto(userId) {
    try {
      // Update profile to remove photo URL
      const updateResult = await this.updateProfileInfo(userId, {
        profile_photo_url: null,
      });

      if (!updateResult.success) {
        throw new Error(updateResult.error);
      }

      return {
        success: true,
        message: "Profile photo removed successfully",
      };
    } catch (error) {
      console.error("Error removing profile photo:", error);
      throw error;
    }
  },

  // Get profile photo upload URL (for direct upload)
  async getProfilePhotoUploadUrl(userId, fileName) {
    try {
      const fileExt = fileName.split(".").pop();
      const uniqueFileName = `${userId}_${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from("profile-photos")
        .createSignedUploadUrl(uniqueFileName, {
          expiresIn: 3600, // 1 hour
        });

      if (error) throw error;

      return {
        success: true,
        uploadUrl: data.signedUrl,
        fileName: uniqueFileName,
      };
    } catch (error) {
      console.error("Error getting upload URL:", error);
      throw error;
    }
  },

  // Update password
  async updatePassword(currentPassword, newPassword) {
    try {
      // First verify current password by attempting to sign in
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.email) {
        throw new Error("User not authenticated");
      }

      // Try to sign in with current password to verify it
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.user.email,
        password: currentPassword,
      });

      if (signInError) {
        throw new Error("Current password is incorrect");
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return {
        success: true,
        message: "Password updated successfully",
      };
    } catch (error) {
      console.error("Error updating password:", error);
      throw error;
    }
  },
};
